import React from 'react';
import Card from '../Card';
import { Tabs } from '../ui';
import { TimeChart, formatNumber, formatPercent, phasesOn, PALETTE } from '../charts';
import PercentileChart, { latencyHasSamples } from './PercentileChart';

/* ── LLM requests: how long each one took and how big it was ───────────────
   The Dashboard's section, and the load tests' report draws the same cards for a phase
   (pages/LoadTests.jsx), from the run's own events (backend loadtest/dashboard_views.py),
   so a reader meets the charts they already know. For the report: `formatX`, `timeline`
   (the run's phases, drawn on each chart: charts/phases.jsx), `gapOnNull` (a bucket with
   no request is a gap, not 0) and `controls` (a source picker in the section head). The
   two metric pairs are controlled: the Dashboard refetches the one card when its pair
   changes.

   Called Latency until Mark renamed it on 2026-10-01, once it held more than latency.
   Two cards per request, side by side: response latency on the left, tokens per request
   on the right (Mark, 2026-09-30, in place of tool call latency). Tool call and turn
   latency moved to the Tools section. All the percentile cards carry the same seven
   percentiles so they can be read against each other.
   Below them (Mark, 2026-10-01): cache hit rate and truncated responses, rates over the
   same requests, then requests per turn across the full width.
   NOTE latency has no history before the deploy that introduced it:
   `usage_logs.latency_ms` did not exist, and proxy-llm read no clock at all. Tokens per
   request has history only for days still in the activity logs on the day it shipped:
   older days were pruned before the rollup recorded token percentiles. */
export default function LlmRequestsSection({
  latency, latencyMetric, onLatencyMetric,
  tokens, tokensMetric, onTokensMetric,
  cacheHitRate, truncated, requestsPerTurn,
  error, controls, formatX, timeline, gapOnNull = false,
}) {
  const cache = cacheHitRate || {};
  const cut = truncated || {};
  const mk = (rows) => phasesOn(rows, timeline);
  return (
    <>
      <div className={`section-head${controls ? ' row-wrap' : ''}`}>
        <h2>LLM requests</h2>
        <p>time, tokens, cache use and cut-offs per request, and requests per user message</p>
        {controls && <div className="ml-auto">{controls}</div>}
      </div>

      {error && (
        /* Named per chart rather than "something went wrong", because an empty percentile
           chart is a legitimate state and the operator otherwise cannot tell the two
           apart. Scoped to this section: the other widgets are unaffected. */
        <div className="notice notice-danger" role="status">
          <div className="notice-body">
            Could not load: {error}. The charts below may be incomplete; the rest of
            this page is unaffected.
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        {/* LLM response latency — total call vs time-to-first-token. */}
        <Card
          title="LLM response latency"
          subtitle={latencyMetric === 'ttft'
            ? 'time to the first streamed token'
            : 'the whole upstream call, measured by proxy-llm'}
          help={latencyMetric === 'ttft'
            ? 'Time to first token is only measurable on a STREAMED response, so it is recorded on the streaming paths and nowhere else — non-streaming calls, compaction sub-calls and the Anthropic branch (which buffers the whole stream before forwarding) contribute no sample. The two metrics therefore cover different populations and their sample counts will not match.'
            : 'The clock starts immediately before the request leaves proxy-llm and stops when the upstream call completes, so this is the model plus the network — not our own auth and parsing. Measured per completion, which means one slow turn made of five calls appears as five samples.'}
          span={6}
          empty={latencyHasSamples(latency?.data) ? null : 'No latency recorded in this window'}
          actions={
            /* Per-graph control, in the Card's own actions slot. A segmented pair rather
               than a dropdown: it is a binary choice and the current state should be
               readable without opening anything. */
            <Tabs size="sm" label="LLM latency metric" value={latencyMetric} onChange={onLatencyMetric}
                  tabs={[
                    { value: 'total', label: 'Total' },
                    { value: 'ttft', label: 'First token' },
                  ]} />
          }>
          <PercentileChart data={latency?.data} formatX={formatX} phases={mk(latency?.data)}
                           gapOnNull={gapOnNull} />
        </Card>

        {/* Tokens per request: the prompt each LLM request read, or what it wrote. The
            Input/Output pair is a per-graph control like the latency card's, so a click
            refetches this card's series only (the Dashboard's fetchLatency). */}
        <Card
          title="Tokens per request"
          subtitle={tokensMetric === 'output'
            ? 'tokens the model wrote, per LLM request'
            : 'prompt tokens the model read, per LLM request, cache included'}
          help={tokensMetric === 'output'
            ? 'One sample per LLM request that reported usage. Output is what the model wrote, reasoning included where the provider counts it, and it is the most expensive token kind. Days pruned before this chart existed have no history here.'
            : 'One sample per LLM request that reported usage. Input is the whole prompt the model read: new input plus cache reads and writes, so a cached request counts at full size even though most of it bills at the cache rate. Every agent step resends it. Days pruned before this chart existed have no history here.'}
          span={6}
          empty={latencyHasSamples(tokens?.data) ? null : 'No requests recorded in this window'}
          actions={
            <Tabs size="sm" label="Tokens per request metric" value={tokensMetric} onChange={onTokensMetric}
                  tabs={[
                    { value: 'input', label: 'Input' },
                    { value: 'output', label: 'Output' },
                  ]} />
          }>
          <PercentileChart data={tokens?.data} format={formatNumber} formatX={formatX}
                           phases={mk(tokens?.data)} gapOnNull={gapOnNull} />
        </Card>

        {/* Cache hit rate: the share of each prompt read from cache, one line per model
            that reports cache use. Models that report none are named under the chart
            rather than drawn as a flat 0%, which would also squash the other lines. */}
        <Card
          title="Cache hit rate"
          subtitle="share of each prompt read from cache, per model"
          help="Cache reads over the whole prompt (new input plus cache reads and writes), per model. A cache read bills at about a tenth of new input on the models that price it, and every agent step resends most of the prompt, so a low rate means most of it is billed again in full. A gap means no requests. Models that report no cache use are named under the chart instead of drawn."
          span={6}
          empty={(cache.models || []).length ? null : 'No cache use reported in this window'}>
          <TimeChart data={cache.data || []} labelKey="date"
                     lines={(cache.models || []).map(m => ({ key: m, label: m }))}
                     colors={(cache.models || []).map((_, i) => PALETTE[(i + 4) % PALETTE.length])}
                     height={240}
                     legend
                     gapOnNull
                     yMax={100}
                     formatY={v => formatPercent(v, 0)}
                     formatValue={v => formatPercent(v, 1)}
                     formatX={formatX}
                     phases={mk(cache.data)} />
          {(cache.window || []).length > 0 && (
            <p className="text-sm text-muted mt-2 mb-0">
              Over the window: {cache.window
                .map(w => `${w.model} ${formatPercent(w.rate, 1)}`).join(' · ')}
            </p>
          )}
          {(cache.no_cache_models || []).length > 0 && (
            <p className="text-sm text-muted mt-1 mb-0">
              Not drawn, no cache use reported: {cache.no_cache_models.join(', ')}
            </p>
          )}
        </Card>

        {/* Truncated responses: requests that stopped at the output limit. One line for
            all models, because a per-model rate over an hour of five requests is noise;
            the models cut off across the whole window are named under the chart. */}
        <Card
          title="Truncated responses"
          subtitle="requests that stopped at the output limit"
          help="Requests whose response ended at the output limit (finish reason length), out of those that recorded a finish reason. A cut-off response can end before the tool call the agent was about to make, so it stops mid-task with no error shown. One line for all models; the note names the models cut off over the window. Days rolled up before this card existed have no data."
          span={6}
          empty={cut.total && cut.total.recorded > 0
            ? null : 'No finish reasons recorded in this window'}>
          <TimeChart data={cut.data || []} labelKey="date"
                     lines={[{ key: 'rate', label: 'Cut off' }]}
                     colors={['var(--danger-solid)']}
                     height={240}
                     gapOnNull
                     formatY={v => formatPercent(v, 1)}
                     formatValue={v => formatPercent(v, 2)}
                     formatX={formatX}
                     phases={mk(cut.data)}
                     tooltipExtra={(row) => (
                       <div className="chart-tooltip-foot">
                         <span className="chart-tooltip-value">{(row.truncated || 0).toLocaleString()}</span>
                         <span className="chart-tooltip-label"> of {(row.recorded || 0).toLocaleString()} requests</span>
                       </div>
                     )} />
          {cut.total && cut.total.recorded > 0 && (
            <p className="text-sm text-muted mt-2 mb-0">
              Over the window: {cut.total.truncated.toLocaleString()} of{' '}
              {cut.total.recorded.toLocaleString()} requests
              ({formatPercent(cut.total.rate, 2)})
              {(cut.models || []).length > 0 && `. ${cut.models.slice(0, 5)
                .map(m => `${m.model} ${formatPercent(m.rate, 1)} (${m.truncated})`).join(' · ')}`}
            </p>
          )}
        </Card>

        {/* Requests per turn: how many LLM requests one user message took, over Turn
            latency's turns. Every agent step resends the prompt, so this is what multiplies
            Tokens per request into the cost of a turn. Full width: it closes the section. */}
        <Card
          title="Requests per turn"
          subtitle="LLM requests behind each user message, until the last reply"
          help="One sample per turn, the same turns as Turn latency (over an hour left out). It counts the user's LLM requests inside the turn: agent steps, the proofreading pass and platform-tool calls, plus any cron or check-in that ran during it. Turns with no LLM request, such as a reply the platform sent itself, are left out. The model and source filters narrow the requests counted."
          span={12}
          empty={latencyHasSamples(requestsPerTurn?.data) ? null : 'No completed turns in this window'}>
          <PercentileChart data={requestsPerTurn?.data} format={formatNumber} height={260}
                           formatX={formatX} phases={mk(requestsPerTurn?.data)} gapOnNull={gapOnNull} />
        </Card>
      </div>
    </>
  );
}
